import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, AlertTriangle, ExternalLink, Tag, Search } from 'lucide-react';
import { marketingBlogAPI } from '../../api/api';
import { fmtNum, fmtPct, fmtPos, fmtFecha } from '../../components/marketing/KpiCard';

const RANGOS = [
  { v: 7, label: '7 días' },
  { v: 28, label: '28 días' },
  { v: 90, label: '90 días' },
];

const FILTROS_FLAG = [
  { v: 'todos', label: 'Todos' },
  { v: 'refresh', label: 'Necesitan refresh' },
  { v: 'sin-tags', label: 'Sin tags' },
  { v: 'sin-trafico', label: 'Sin impresiones' },
];

export default function MarketingBlogInventario() {
  const navigate = useNavigate();
  const [dias, setDias] = useState(28);
  const [posts, setPosts] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [forceScrape, setForceScrape] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [categoria, setCategoria] = useState('todas');
  const [filtroFlag, setFiltroFlag] = useState('todos');
  const [orden, setOrden] = useState({ campo: 'impressions', asc: false });

  const cargar = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await marketingBlogAPI.inventory({ dias });
      setPosts(res.data.posts);
      setMeta({ desde: res.data.desde, hasta: res.data.hasta, total: res.data.total });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error cargando inventario');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [dias]);

  const sync = async () => {
    setSyncing(true);
    setError(null);
    try {
      const res = await marketingBlogAPI.inventorySync(forceScrape);
      const r = res.data;
      alert(
        `✅ Sync completo en ${r.duracion_s}s:\n` +
        `· ${r.posts_sincronizados} posts en RSS\n` +
        `· ${r.nuevos} nuevos | ${r.actualizados} actualizados\n` +
        `· ${r.scrapeados} scrapeados\n` +
        (r.draft_marcados ? `· ${r.draft_marcados} marcados como draft\n` : '') +
        (r.errores?.length ? `\n⚠ ${r.errores.length} errores` : '')
      );
      await cargar();
    } catch (e) {
      alert('Error sincronizando: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSyncing(false);
    }
  };

  const categorias = useMemo(() => {
    const set = new Set();
    for (const p of posts) {
      (p.categories || []).forEach((c) => set.add(c));
      if (p.category) set.add(p.category);
    }
    return ['todas', ...Array.from(set).sort()];
  }, [posts]);

  const visibles = useMemo(() => {
    let arr = posts.slice();
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase();
      arr = arr.filter((p) => (p.title || '').toLowerCase().includes(q) || (p.slug || '').toLowerCase().includes(q));
    }
    if (categoria !== 'todas') {
      arr = arr.filter((p) =>
        (p.categories || []).includes(categoria) || p.category === categoria
      );
    }
    if (filtroFlag === 'refresh') arr = arr.filter((p) => p.flags.necesita_refresh);
    if (filtroFlag === 'sin-tags') arr = arr.filter((p) => !p.flags.tiene_tags);
    if (filtroFlag === 'sin-trafico') arr = arr.filter((p) => (p.metrics.impressions || 0) === 0);

    arr.sort((a, b) => {
      const getter = (p) => {
        switch (orden.campo) {
          case 'title': return (p.title || '').toLowerCase();
          case 'pub_date': return p.pub_date || '';
          case 'updated_date': return p.updated_date || p.pub_date || '';
          case 'impressions': return p.metrics.impressions || 0;
          case 'clicks': return p.metrics.clicks || 0;
          case 'ctr': return p.metrics.ctr || 0;
          case 'position': return p.metrics.position || 999;
          case 'sessions': return p.metrics.sessions || 0;
          default: return 0;
        }
      };
      const va = getter(a), vb = getter(b);
      if (va < vb) return orden.asc ? -1 : 1;
      if (va > vb) return orden.asc ? 1 : -1;
      return 0;
    });
    return arr;
  }, [posts, busqueda, categoria, filtroFlag, orden]);

  const toggleOrden = (campo) => {
    setOrden((prev) => prev.campo === campo
      ? { campo, asc: !prev.asc }
      : { campo, asc: campo === 'title' || campo === 'pub_date' || campo === 'position' }
    );
  };

  const TH = ({ campo, children, align = 'left' }) => (
    <th
      onClick={() => toggleOrden(campo)}
      className={`py-2 px-2 font-medium text-${align} cursor-pointer hover:bg-gray-50 select-none whitespace-nowrap`}
    >
      {children}
      {orden.campo === campo && <span className="ml-1 text-duetto-primary">{orden.asc ? '▲' : '▼'}</span>}
    </th>
  );

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 bg-white border rounded-lg p-3">
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          {RANGOS.map((r) => (
            <button
              key={r.v}
              onClick={() => setDias(r.v)}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                dias === r.v ? 'bg-white text-duetto-primary shadow-sm font-semibold' : 'text-gray-600 hover:text-gray-900'
              }`}
            >{r.label}</button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por título o slug…"
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-duetto-primary"
          />
        </div>
        <select
          value={categoria}
          onChange={(e) => setCategoria(e.target.value)}
          className="text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-duetto-primary"
        >
          {categorias.map((c) => <option key={c} value={c}>{c === 'todas' ? 'Todas las categorías' : c}</option>)}
        </select>
        <select
          value={filtroFlag}
          onChange={(e) => setFiltroFlag(e.target.value)}
          className="text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-duetto-primary"
        >
          {FILTROS_FLAG.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
        </select>
        <div className="flex-1" />
        <label className="text-xs text-gray-600 flex items-center gap-1">
          <input
            type="checkbox"
            checked={forceScrape}
            onChange={(e) => setForceScrape(e.target.checked)}
          />
          Force scrape (más lento)
        </label>
        <button
          onClick={sync}
          disabled={syncing}
          className="flex items-center gap-2 px-3 py-1.5 bg-duetto-primary text-white text-sm rounded-md hover:bg-opacity-90 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Sincronizando…' : 'Sync inventario'}
        </button>
      </div>

      {/* Estado / contador */}
      <div className="flex items-center justify-between text-xs text-gray-500 px-1">
        <span>
          {meta && (
            <>
              {visibles.length} de {posts.length} posts · ventana {meta.desde} → {meta.hasta}
            </>
          )}
        </span>
        {error && <span className="text-red-600">{error}</span>}
      </div>

      {/* Tabla */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 border-b bg-gray-50">
              <tr>
                <TH campo="title">Post</TH>
                <TH campo="pub_date">Publicado</TH>
                <TH campo="updated_date">Actualizado</TH>
                <TH campo="impressions" align="right">Impr.</TH>
                <TH campo="clicks" align="right">Clicks</TH>
                <TH campo="ctr" align="right">CTR</TH>
                <TH campo="position" align="right">Pos.</TH>
                <TH campo="sessions" align="right">Ses.</TH>
                <th className="py-2 px-2 font-medium text-center">Flags</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="text-center text-gray-500 py-8">Cargando posts…</td></tr>
              )}
              {!loading && visibles.length === 0 && (
                <tr><td colSpan={9} className="text-center text-gray-500 py-8">
                  Sin posts con los filtros aplicados. Pulsa "Sync inventario" si la BD está vacía.
                </td></tr>
              )}
              {visibles.map((p) => (
                <tr
                  key={p.slug}
                  className="border-b last:border-0 hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/marketing/blog/posts/${p.slug}`)}
                  title="Ver detalle del post"
                >
                  <td className="py-2 px-2">
                    <div className="flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-gray-900 font-medium truncate" title={p.title}>{p.title}</div>
                        <div className="flex items-center gap-2 mt-0.5">
                          {(p.categories || []).slice(0, 2).map((c) => (
                            <span key={c} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{c}</span>
                          ))}
                          <a
                            href={p.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-[10px] text-duetto-primary hover:underline inline-flex items-center gap-0.5"
                            title="Ver en la web"
                          >
                            <ExternalLink className="w-3 h-3" />
                            ver
                          </a>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 px-2 text-gray-700 whitespace-nowrap">{fmtFecha(p.pub_date)}</td>
                  <td className="py-2 px-2 text-gray-700 whitespace-nowrap">{fmtFecha(p.updated_date)}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{fmtNum(p.metrics.impressions)}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{fmtNum(p.metrics.clicks)}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{fmtPct(p.metrics.ctr)}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{fmtPos(p.metrics.position)}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{fmtNum(p.metrics.sessions)}</td>
                  <td className="py-2 px-2 text-center whitespace-nowrap">
                    {p.flags.necesita_refresh && (
                      <span title="Necesita refresh" className="inline-flex items-center gap-0.5 text-[10px] bg-amber-50 border border-amber-200 text-amber-700 px-1.5 py-0.5 rounded mr-1">
                        <AlertTriangle className="w-3 h-3" /> refresh
                      </span>
                    )}
                    {!p.flags.tiene_tags && (
                      <span title="Sin tags" className="inline-flex items-center gap-0.5 text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                        <Tag className="w-3 h-3" /> sin tags
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
